import type { ReactElement, ReactNode } from "react";

import { Aura } from "./Aura";
import { ToolbarRegion } from "./Toolbar";

/**
 * Das **Seitengerüst**: die Sidebar links über die volle Fensterhöhe, rechts
 * daneben die Kopfleiste und darunter die Inhaltsfläche.
 *
 * Es gehört zum Rahmen (ADR-0025) und wird nachgezeichnet, nicht komponiert:
 * Vorlage ist der Zusammenbau in `design/reference/index.html`, beschrieben in
 * `FRAME.md` unter „Der Zusammenbau" und „Das Seitengerüst".
 *
 * **Die Achsen sind die häufigste Fehlstelle.** `FRAME.md` nennt zwei
 * Strukturfehler: das Anmeldebild in der Hülle (der steht in `App.tsx`) und die
 * Kopfleiste über der Sidebar. Hier war der zweite gebaut — ein Band über die
 * volle Breite mit der Navigation darunter (#40). Richtig ist: Die Sidebar ist
 * erstes Flex-Kind und läuft über die volle Höhe, und die Kopfleiste **beginnt,
 * wo die Sidebar endet**.
 *
 * Die Sidebar kommt als `sidebar` und die Kopfleiste als `topBar` herein und
 * nicht als Kinder: Zwischen beiden verläuft die Naht dieses Gerüsts. Die
 * Inhaltsfläche beginnt **unter** der Kopfleiste, und ihre abgerundete Ecke ist
 * dort sichtbar — läge die Leiste darin, wäre sie es nicht.
 *
 * **Weder Sidebar noch Kopfleiste tragen eine eigene Trennlinie.** Die eine Kante
 * des Gerüsts ist die der Inhaltsfläche: `--r-surface` oben links, darüber eine
 * Haarlinie in `--line-f` als eigenes Element ohne Zeigerannahme. `FRAME.md`
 * nennt das „das Detail, an dem man die Anwendung erkennt", und es ist zugleich
 * der Grund, warum die beiden Leisten ohne Linie auskommen.
 *
 * **Das Gerüst scrollt nicht** — der Inhalt scrollt darin. Die Inhaltsfläche ist
 * zugleich der Behälter der **Werkzeugleiste** (#41): Sie ist `overflow: hidden`
 * und oben links gerundet, die Leiste ist ihr erstes Flex-Kind und wiederholt
 * dieselbe Rundung (`reference/AppShell.jsx`, `Toolbar`). Damit beginnt der
 * Inhalt 113px unter dem Fensterrand — `--h-topbar` (64) und `--h-toolbar` (49).
 * Die Rundung ist nicht wegzunehmen mit dem Argument, die Leiste bringe sie mit.
 *
 * **Den Platz stellt das Gerüst, den Inhalt die Ansicht.** `ToolbarRegion` ist
 * die Klammer dafür; sie zeichnet von sich aus nichts. Ein `toolbar`-Prop hier
 * hätte die Leiste in `App.tsx` gezogen, wo der Zustand nicht liegt, den sie
 * bedient — welcher Abschnitt der Verwaltung offen ist, weiß die Verwaltung.
 */
export function Shell({
  sidebar,
  topBar,
  children,
}: {
  sidebar: ReactNode;
  topBar: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        overflow: "hidden",
        background: "var(--bg-app)",
        color: "var(--text-body)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <Aura />
      {sidebar}

      {/* Die Spalte rechts neben der Sidebar. Sie trägt die Fläche der Leisten;
          die Inhaltsfläche darin setzt ihre eigene darauf. */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-chrome)",
        }}
      >
        {topBar}

        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-app)",
            borderTopLeftRadius: "var(--r-surface)",
            overflow: "hidden",
          }}
        >
          {/* Der Platz der **Werkzeugleiste** (#41): das erste Flex-Kind dieser
              Fläche, vor der scrollenden. Er trägt nichts von sich aus — welche
              Knöpfe darin stehen, weiß die Ansicht, und ein Screen ohne Aktionen
              bekommt kein leeres Glasband. Deshalb steht hier eine Klammer und
              kein `toolbar`-Prop: Die Leiste käme sonst aus `App.tsx`, und der
              Zustand, den sie bedient, liegt in der Ansicht. */}
          <ToolbarRegion>
            <main
              className="erp-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                justifyContent: "center",
                // flex-start, sonst zieht ein kurzer Bereich sich über die ganze Höhe
                alignItems: "flex-start",
                padding: "var(--sp-10)",
              }}
            >
              {children}
            </main>
          </ToolbarRegion>

          {/* Die Kante als eigenes Element und nicht als Rahmenlinie der Fläche:
              Ein Rahmen rückte den Inhalt um einen Punkt ein und ließe sich vom
              Rollbalken darunter durchschneiden. Sie liegt über allem und fängt
              nichts ab. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              borderTop: "1px solid var(--line-f)",
              borderLeft: "1px solid var(--line-f)",
              borderTopLeftRadius: "var(--r-surface)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
